import { SignIn, useAuth } from "@clerk/react";
import { LockIcon } from "lucide-react";
import { useEffect, type ReactNode } from "react";
import { useShallow } from "zustand/react/shallow";

import { APP_DISPLAY_NAME } from "~/branding";
import { clerkProviderEnabled } from "~/cloud/clerkRuntime";
import { Button } from "../ui/button";
import { Spinner } from "../ui/spinner";
import {
  selectHostedAccessRecord,
  syncHostedAccessAccount,
  useHostedAccessStore,
} from "../../hosted/hostedAccessStore";
import { syncHostedProjectsAccount } from "../../hosted/hostedProjectStore";
import { resolveHostedOwnerGateState } from "../../hosted/hostedMode";

/**
 * Owner gate for the hosted deployment.
 *
 * The Worker is authoritative: it verifies the Clerk session and answers
 * `403 owner_only` for anyone who is not the configured owner. This component
 * only reflects that answer, so no user id or secret ships in the bundle.
 */
export function HostedOwnerGate({ children }: { readonly children: ReactNode }) {
  // Calling Clerk hooks without a provider throws, so a build with no provider
  // renders through untouched (local/dev T3, and hosted builds with no key,
  // where this gate is moot because nothing can reach the hosted API anyway).
  if (!clerkProviderEnabled()) {
    return children;
  }

  return <ConfiguredHostedOwnerGate>{children}</ConfiguredHostedOwnerGate>;
}

function ConfiguredHostedOwnerGate({ children }: { readonly children: ReactNode }) {
  const { isLoaded, isSignedIn, signOut, userId } = useAuth({ treatPendingAsSignedOut: false });
  const accountId = isSignedIn && userId ? userId : null;
  // Reading the record for this account rather than the raw fields means a
  // denial recorded for a previous account never reaches this render, even
  // before the sync effect below runs.
  const { errorCode, errorMessage } = useHostedAccessStore(
    useShallow((state) => selectHostedAccessRecord(state, accountId)),
  );

  // Sign-out and account switches invalidate both the Worker's last answer and
  // everything cached under it. The access record follows the session so a stale
  // `owner_only` cannot keep a valid owner out until a reload, and the project
  // cache follows it so the next account never sees the previous account's
  // projects.
  useEffect(() => {
    if (!isLoaded) {
      return;
    }
    syncHostedAccessAccount(accountId);
    syncHostedProjectsAccount(accountId);
  }, [accountId, isLoaded]);

  const gate = resolveHostedOwnerGateState({
    clerkConfigured: true,
    clerkLoaded: isLoaded,
    signedIn: isSignedIn ?? false,
    apiErrorCode: errorCode,
    apiErrorMessage: errorMessage,
  });

  if (gate.kind === "allowed" || gate.kind === "clerk-unconfigured") {
    return children;
  }

  if (gate.kind === "loading") {
    return (
      <GateShell>
        <p className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
          <Spinner className="size-4" />
          Checking your session…
        </p>
      </GateShell>
    );
  }

  if (gate.kind === "signed-out") {
    return (
      <GateShell>
        <div className="flex justify-center" data-testid="hosted-owner-gate-sign-in">
          <SignIn
            // Hash routing keeps every sign-in step (factor two, reset) inside
            // this gate without needing a catch-all route, and the gate replaces
            // the whole app while signed out so there is nothing to preserve.
            routing="hash"
            appearance={{
              elements: {
                rootBox: "w-full",
                cardBox: "w-full shadow-none",
              },
            }}
          />
        </div>
      </GateShell>
    );
  }

  return (
    <GateShell>
      <div className="text-center" data-testid="hosted-owner-gate-denied" role="alert">
        <span className="mx-auto mb-4 grid size-10 place-items-center rounded-xl border border-border/70 bg-background/70 text-muted-foreground">
          <LockIcon className="size-4" />
        </span>
        <h1 className="text-base font-medium text-foreground">Private alpha</h1>
        <p className="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-muted-foreground/80">
          {gate.message}
        </p>
        <div className="mt-5 flex justify-center">
          <Button size="sm" variant="outline" onClick={() => void signOut()}>
            Sign out
          </Button>
        </div>
      </div>
    </GateShell>
  );
}

function GateShell({ children }: { readonly children: ReactNode }) {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-background px-4 py-10 text-foreground">
      <div className="w-full max-w-md">
        <p className="mb-6 text-center text-[11px] font-medium tracking-[0.18em] text-muted-foreground/70 uppercase">
          {APP_DISPLAY_NAME}
        </p>
        {children}
      </div>
    </div>
  );
}
